import type { Timestamp } from 'firebase/firestore';
import {
  CorrelationOptIns,
  DEFAULT_CORRELATION_OPTINS,
} from './correlations';

/**
 * Daily Bite — daily tracking data model.
 *
 * One document per member per day at users/{uid}/daily_entries/{YYYY-MM-DD}.
 * See docs/markdown/eng/MELIO_DAILY_TRACKING_SPEC.md for the full spec.
 */

export type TrackerId =
  | 'water'
  | 'protein'
  | 'caffeine'
  | 'alcohol'
  | 'nicotine'
  | 'mood'
  | 'gratitude'
  | 'digestion';

export type NicotineForm = 'cigarette' | 'vape' | 'pouch' | 'gum' | 'patch' | 'cigar_pipe';

export interface WaterEntry {
  totalMl: number;
}

export interface ProteinServing {
  label: string;
  grams: number;
  loggedAt: Timestamp | null;
}

export interface ProteinEntry {
  totalGrams: number;
  /** Reserved for phase 2 timed/occasion logging. Sliders only set totals. */
  servings?: ProteinServing[];
}

export interface CaffeineOccasion {
  source: string;
  mg: number;
  loggedAt: Timestamp | null;
}

export interface CaffeineEntry {
  totalMg: number;
  /**
   * Timestamp of the most recent caffeine intake today. Captured because
   * caffeine timing (not just total) drives the sleep-quality correlation we
   * want to surface in phase 3. Auto-stamped when the slider moves; member
   * can correct via the time picker.
   */
  lastAt?: Timestamp | null;
  /** Reserved for phase 3 per-occasion logging if we need sub-day precision. */
  occasions?: CaffeineOccasion[];
}

export interface AlcoholDrink {
  drinkType: string;
  units: number;
  loggedAt: Timestamp | null;
}

export interface AlcoholEntry {
  units: number;
  /**
   * Timestamp of the most recent drink today. Drives HRV / sleep correlations
   * in phase 3 the same way caffeine.lastAt does. Auto-stamped on slider
   * commit; editable. Left undefined on dry days.
   */
  lastAt?: Timestamp | null;
  /** Reserved for phase 3 per-occasion logging. */
  drinks?: AlcoholDrink[];
}

export interface NicotineRecord {
  form: NicotineForm;
  amount: number;
  strengthMg?: number;
  estimatedMg: number;
  loggedAt: Timestamp | null;
}

export interface NicotineEntry {
  estimatedTotalMg: number;
  /** Same timing-correlation rationale as caffeine + alcohol. */
  lastAt?: Timestamp | null;
  entries: NicotineRecord[];
}

export interface MoodEntry {
  valence: number;
  energy: number;
  tags: string[];
  loggedAt: Timestamp | null;
}

export interface GratitudeEntry {
  prompt: string;
  response: string;
  loggedAt: Timestamp | null;
}

export interface BristolMovement {
  bristol: number;
  loggedAt: Timestamp | null;
}

export interface DigestionEntry {
  movements: BristolMovement[];
}

export interface CustomHabitStatus {
  id: string;
  label: string;
  done: boolean;
}

export interface DailyEntryRecord {
  id: string;
  date: string;
  water?: WaterEntry;
  protein?: ProteinEntry;
  caffeine?: CaffeineEntry;
  alcohol?: AlcoholEntry;
  nicotine?: NicotineEntry;
  mood?: MoodEntry;
  gratitude?: GratitudeEntry;
  digestion?: DigestionEntry;
  customHabits?: CustomHabitStatus[];
  completeness: number;
  lockedAt?: Timestamp | null;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export type TrackerProfile = 'standard' | 'pregnant' | 'peri_menopause' | 'post_op';
export type UnitSystem = 'metric' | 'imperial';

export interface TrackerOptedIn {
  water: boolean;
  protein: boolean;
  caffeine: boolean;
  alcohol: boolean;
  nicotine: boolean;
  mood: boolean;
  gratitude: boolean;
  digestion: boolean;
}

export interface ClinicianSharing {
  water: boolean;
  protein: boolean;
  caffeine: boolean;
  alcohol: boolean;
  nicotine: boolean;
  mood: boolean;
  gratitude: boolean;
  digestion: boolean;
  weeklyHabits: boolean;
}

export interface PartnerSharing {
  enabled: boolean;
  streaksOnly: true;
  partnerEmail?: string;
  partnerDisplayName?: string;
}

export interface TrackerReminders {
  dailyAtHome: boolean;
  eveningReflection: boolean;
}

export interface StreakFreezes {
  available: number;
  usedThisMonth: number;
}

/**
 * Custom pill saved by the member, e.g. "My protein shake · 42 g" or
 * "Morning latte · 120 mg". Values are stored in the tracker's canonical unit
 * (ml / g / mg). Tapping a pill adds `amount` to the current slider value —
 * never replaces — so the member can stack two coffees by tapping twice.
 */
export interface CustomPill {
  id: string;
  label: string;
  amount: number;
  createdAt?: Timestamp | null;
}

export interface CustomPillsByTracker {
  water?: CustomPill[];
  protein?: CustomPill[];
  caffeine?: CustomPill[];
}

export interface TrackerSettingsRecord {
  profile: TrackerProfile;
  optedIn: TrackerOptedIn;
  nicotineForms: NicotineForm[];
  unitSystem: UnitSystem;
  sharing: {
    clinician: ClinicianSharing;
    partner: PartnerSharing;
  };
  reminders: TrackerReminders;
  streakFreezes: StreakFreezes;
  customHabits: Array<{ id: string; label: string; createdAt?: Timestamp | null }>;
  customPills?: CustomPillsByTracker;
  /**
   * Per-correlation opt-ins. Lives here alongside clinician + partner
   * sharing so the whole "what gets computed / seen" surface is one doc.
   * Defaults follow the same safe-on / sensitive-off pattern as clinician
   * sharing — see correlations.ts for the default map.
   */
  correlations?: CorrelationOptIns;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export const DEFAULT_TRACKER_SETTINGS: TrackerSettingsRecord = {
  profile: 'standard',
  optedIn: {
    water: true,
    protein: true,
    caffeine: true,
    alcohol: true,
    nicotine: false,
    mood: true,
    gratitude: true,
    digestion: true,
  },
  nicotineForms: [],
  unitSystem: 'metric',
  sharing: {
    clinician: {
      water: true,
      protein: true,
      caffeine: true,
      alcohol: false,
      nicotine: true,
      mood: false,
      gratitude: false,
      digestion: false,
      weeklyHabits: true,
    },
    partner: {
      enabled: false,
      streaksOnly: true,
    },
  },
  reminders: {
    dailyAtHome: true,
    eveningReflection: true,
  },
  streakFreezes: {
    available: 2,
    usedThisMonth: 0,
  },
  customHabits: [],
  customPills: {
    water: [],
    protein: [],
    caffeine: [],
  },
  correlations: { ...DEFAULT_CORRELATION_OPTINS },
};

export const TRACKER_LABELS: Record<TrackerId, string> = {
  water: 'Water',
  protein: 'Protein',
  caffeine: 'Caffeine',
  alcohol: 'Alcohol',
  nicotine: 'Nicotine',
  mood: 'Mood',
  gratitude: 'Gratitude',
  digestion: 'Digestion',
};

/* ────────────────────────────────────────────────────────────────────────────
   Tracker summary — the materialised streak + badge state written after every
   daily entry save. Lives at users/{uid}/tracker_summary/current.

   Design notes:
   - Freezes are scoped to the calendar month. Budget is 2/month; we track
     which days in the current month were "frozen" to avoid double-spending.
   - `currentStreak` is the consecutive run of logged days (any tracker with
     completeness > 0) ending today or yesterday, with freezes filling gaps.
   - `earnedBadges` is append-only: once earned, the badge stays even if the
     underlying condition lapses. Re-earned badges update `mostRecentAt`.
   - `lastComputedForDate` lets the client detect stale summaries (e.g. if the
     page is left open past midnight).
   ──────────────────────────────────────────────────────────────────────── */

export type BadgeCategory = 'milestone' | 'narrative' | 'identity';

export type BadgeId =
  | 'first_bite'
  | 'first_week'
  | 'first_month'
  | 'honest_logger'
  | 'hydration_habit'
  | 'caffeine_aware_2wk'
  | 'dry_week'
  | 'mood_moment'
  | 'gratitude_week'
  | 'no_nicotine_identity'
  | 'no_alcohol_identity';

export interface EarnedBadge {
  id: BadgeId;
  earnedAt: Timestamp;
  mostRecentAt?: Timestamp;
}

export interface BadgeProgress {
  id: BadgeId;
  currentValue: number;
  targetValue: number;
  /** Free-text next-step hint, e.g. "3 more dry days". */
  hint?: string;
}

export interface TrackerSummaryRecord {
  currentStreak: number;
  longestStreak: number;
  freezesAvailable: number;
  freezesUsedThisMonth: number;
  freezeDaysAppliedThisMonth: string[];
  month: string;
  earnedBadges: EarnedBadge[];
  upcomingBadges: BadgeProgress[];
  lastComputedForDate: string;
  lastComputedAt?: Timestamp | null;
}

export const EMPTY_TRACKER_SUMMARY: TrackerSummaryRecord = {
  currentStreak: 0,
  longestStreak: 0,
  freezesAvailable: 2,
  freezesUsedThisMonth: 0,
  freezeDaysAppliedThisMonth: [],
  month: '',
  earnedBadges: [],
  upcomingBadges: [],
  lastComputedForDate: '',
  lastComputedAt: null,
};

/* ────────────────────────────────────────────────────────────────────────────
   Weekly reflection — Sunday-evening recap surfaced on /today and /home, and
   sent as an email on Monday morning. One document per ISO week, lazily
   generated when the member first opens the app on Sunday or later, and
   read by the email Cloud Function on Monday so we send the same content
   the member has already seen in-app.

   Lives at users/{uid}/weekly_reflections/{weekId} where weekId is
   "YYYY-Www" (e.g. "2026-W17"). Idempotent — re-computing in the same week
   overwrites in place.

   Tone: explicitly non-judgmental, framed around what the data MEANS, not a
   pass/fail score. Insights are only added if true and gentle. We never
   moralise (no "you drank too much"); we observe ("you had caffeine after
   3pm on N days"). See the spec for the full template library.
   ──────────────────────────────────────────────────────────────────────── */

export interface WeeklyReflectionInsight {
  id: string;
  text: string;
}

export interface WeeklyReflectionRecord {
  weekId: string;
  weekStart: string;
  weekEnd: string;
  daysLogged: number;
  streakAtEnd: number;
  mostLoggedTracker: TrackerId | null;
  mostSkippedTracker: TrackerId | null;
  leadParagraph: string;
  insights: WeeklyReflectionInsight[];
  generatedAt?: Timestamp | null;
  dismissedAt?: Timestamp | null;
  emailedAt?: Timestamp | null;
}
